// features/workstations/think/think-home.js
// ═══════════════════════════════════════════════════════════
// THINK TAB HOMEPAGE — thread library
// ═══════════════════════════════════════════════════════════
// Landing surface when the user clicks Think. Two-per-row
// card grid of all threads in this workstation. Each card
// is a self-contained conversation — its own chat history,
// canvas, journey map, artifacts.
//
// Header:
//   - "Threads in [Workstation Name]"
//   - Continue [most recent] → if any threads exist
//   - + New Thread button (creates + opens immediately)
//
// Card actions:
//   - Click body → opens the thread (parent's onOpenThread)
//   - Three-dot menu → Rename | Delete (with confirm)
// ═══════════════════════════════════════════════════════════

import { useEffect, useState } from 'react';
import ThreadCard from './thread-card';

export default function ThinkHome({ workstation, userId, onOpenThread }) {
  const [threads, setThreads] = useState([]);
  const [loading, setLoading] = useState(true);
  const [creating, setCreating] = useState(false);
  const [error, setError] = useState(null);
  const [reloadKey, setReloadKey] = useState(0);

  useEffect(() => {
    if (!workstation?.id || !userId) {
      setLoading(false);
      return;
    }
    let cancelled = false;
    setLoading(true);
    setError(null);
    (async () => {
      try {
        const qs = new URLSearchParams({
          workstation_id: workstation.id,
          user_id: userId,
        });
        const res = await fetch(`/api/workstations/think-thread?${qs}`, { cache: 'no-store' });
        if (!res.ok) {
          const body = await res.json().catch(() => ({}));
          throw new Error(body?.error || `HTTP ${res.status}`);
        }
        const data = await res.json();
        if (cancelled) return;
        setThreads(data.threads || []);
      } catch (e) {
        if (!cancelled) setError(e?.message || 'Failed to load threads');
      } finally {
        if (!cancelled) setLoading(false);
      }
    })();
    return () => { cancelled = true; };
  }, [workstation?.id, userId, reloadKey]);

  async function createNewThread() {
    if (creating) return;
    setCreating(true);
    setError(null);
    try {
      const res = await fetch('/api/workstations/think-thread?action=new', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ workstation_id: workstation.id, user_id: userId }),
      });
      if (!res.ok) {
        const body = await res.json().catch(() => ({}));
        throw new Error(body?.error || `HTTP ${res.status}`);
      }
      const data = await res.json();
      // Open the freshly created thread immediately.
      onOpenThread?.(data.thread.id);
    } catch (e) {
      setError(e?.message || 'Failed to create thread');
    } finally {
      setCreating(false);
    }
  }

  async function renameThread(threadId, currentTitle) {
    const next = window.prompt('Rename thread:', currentTitle || '');
    if (next == null) return;
    const trimmed = next.trim();
    if (!trimmed || trimmed === currentTitle) return;
    try {
      const res = await fetch('/api/workstations/think-thread?action=rename', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          workstation_id: workstation.id,
          user_id: userId,
          thread_id: threadId,
          title: trimmed,
        }),
      });
      if (!res.ok) throw new Error(`HTTP ${res.status}`);
      setReloadKey((k) => k + 1);
    } catch (e) {
      setError(`Rename failed: ${e?.message}`);
    }
  }

  async function deleteThread(threadId, title) {
    const ok = window.confirm(
      `Delete thread "${title || 'Untitled'}"?\n\nPermanently removes its chat, canvas, artifacts, and journey nodes. Cannot be undone.`,
    );
    if (!ok) return;
    try {
      const qs = new URLSearchParams({
        workstation_id: workstation.id,
        user_id: userId,
        thread_id: threadId,
      });
      const res = await fetch(`/api/workstations/think-thread?${qs}`, { method: 'DELETE' });
      if (!res.ok) {
        const body = await res.json().catch(() => ({}));
        throw new Error(body?.error || `HTTP ${res.status}`);
      }
      setReloadKey((k) => k + 1);
    } catch (e) {
      setError(`Delete failed: ${e?.message}`);
    }
  }

  const mostRecent = threads[0] || null;

  return (
    <div className="ws-think-home">
      <div className="ws-think-home-header">
        <div>
          <div className="ws-think-home-eyebrow">Think</div>
          <div className="ws-think-home-title">
            Threads in <em>{workstation?.name || 'this workstation'}</em>
          </div>
          <div className="ws-think-home-sub">
            Each thread is its own conversation — own chat, own canvas,
            own journey. Cross-thread memory keeps the AI in continuity.
          </div>
        </div>
        <div className="ws-think-home-actions">
          {mostRecent && (
            <button
              type="button"
              className="ws-think-home-continue"
              onClick={() => onOpenThread?.(mostRecent.id)}
              title={`Continue: ${mostRecent.title || 'Untitled'}`}
            >
              Continue <span className="ws-think-home-continue-name">
                {(mostRecent.title || 'Untitled').slice(0, 32)}
              </span> →
            </button>
          )}
          <button
            type="button"
            className="ws-think-home-new"
            onClick={createNewThread}
            disabled={creating}
          >
            {creating ? 'Creating…' : '+ New Thread'}
          </button>
        </div>
      </div>

      {error && <div className="ws-form-error" style={{ marginBottom: 14 }}>{error}</div>}

      {loading && <div className="ws-loading">Loading threads…</div>}

      {!loading && threads.length === 0 && (
        <div className="ws-think-home-empty">
          <div className="ws-think-home-empty-eyebrow">No threads yet</div>
          <div className="ws-think-home-empty-title">Start your first conversation</div>
          <div className="ws-think-home-empty-body">
            A thread holds one conversation, its own canvas, its own
            artifacts. Use threads as you'd use notebooks — one per
            decision, project, or question worth thinking through.
          </div>
          <button
            type="button"
            className="ws-think-home-new"
            onClick={createNewThread}
            disabled={creating}
          >
            {creating ? 'Creating…' : '+ New Thread'}
          </button>
        </div>
      )}

      {!loading && threads.length > 0 && (
        <div className="ws-thread-grid">
          {threads.map((t) => (
            <ThreadCard
              key={t.id}
              thread={t}
              onOpen={() => onOpenThread?.(t.id)}
              onRename={() => renameThread(t.id, t.title)}
              onDelete={() => deleteThread(t.id, t.title)}
            />
          ))}
        </div>
      )}
    </div>
  );
}
